/**
 * Configuration Panel Component
 *
 * Right sidebar for configuring selected workflow steps.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { JsonEditor } from '../ui/json-editor';
import { CodeEditor } from '../ui/code-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { CanvasStep } from './WorkflowCanvas';
import { WorkflowCondition } from '../../types/workflow';

/**
 * Props for the ConfigurationPanel component
 */
interface ConfigurationPanelProps {
  step: CanvasStep | null;
  onUpdateStep: (stepId: string, updates: Partial<CanvasStep>) => void;
  onClose: () => void;
}

/**
 * Configuration Panel Component
 */
export function ConfigurationPanel({
  step,
  onUpdateStep,
  onClose,
}: ConfigurationPanelProps) {
  const [localConfig, setLocalConfig] = useState<Record<string, unknown>>({});
  const [localConditions, setLocalConditions] = useState<WorkflowCondition[]>([]);

  // Sync local state with step
  useEffect(() => {
    if (step) {
      setLocalConfig(step.config || {});
      setLocalConditions(step.conditions || []);
    }
  }, [step]);

  if (!step) {
    return (
      <div className="w-80 bg-white border-l border-slate-200 p-6">
        <div className="text-center text-muted-foreground py-12">
          <div className="text-4xl mb-4">⚙️</div>
          <p className="text-sm">Select a step to configure it</p>
        </div>
      </div>
    );
  }

  const handleNameChange = (name: string) => {
    onUpdateStep(step.id, { name });
  };

  const handleConfigChange = (key: string, value: unknown) => {
    const newConfig = { ...localConfig, [key]: value };
    setLocalConfig(newConfig);
    onUpdateStep(step.id, { config: newConfig });
  };

  const handleAddCondition = () => {
    const newCondition: WorkflowCondition = {
      field: '',
      operator: 'equals',
      value: '',
    };
    const newConditions = [...localConditions, newCondition];
    setLocalConditions(newConditions);
    onUpdateStep(step.id, { conditions: newConditions });
  };

  const handleUpdateCondition = (
    index: number,
    updates: Partial<WorkflowCondition>
  ) => {
    const newConditions = localConditions.map((c, i) =>
      i === index ? { ...c, ...updates } : c
    );
    setLocalConditions(newConditions);
    onUpdateStep(step.id, { conditions: newConditions });
  };

  const handleRemoveCondition = (index: number) => {
    const newConditions = localConditions.filter((_, i) => i !== index);
    setLocalConditions(newConditions);
    onUpdateStep(step.id, { conditions: newConditions });
  };

  return (
    <div className="w-80 bg-white border-l border-slate-200 overflow-y-auto">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <h2 className="font-semibold">Step Configuration</h2>
        <button
          onClick={onClose}
          className="p-1 hover:bg-slate-100 rounded transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="p-4 space-y-6">
        {/* Step Header */}
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center text-2xl border border-slate-200">
            {step.icon}
          </div>
          <div>
            <p className="font-medium">{step.name}</p>
            <p className="text-xs text-muted-foreground capitalize">
              {step.stepType} step
            </p>
          </div>
        </div>

        {/* Basic Settings */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm">Basic Settings</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="step-name">Step Name</Label>
              <Input
                id="step-name"
                value={step.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Enter step name"
              />
            </div>
          </CardContent>
        </Card>

        {/* Agent-specific Settings */}
        {step.stepType === 'agent' && (
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm">Agent Settings</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0 space-y-4">
              <div className="space-y-2">
                <Label>Agent</Label>
                <p className="text-sm text-muted-foreground bg-slate-50 p-2 rounded">
                  {step.agentName}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Input Mapping (JSON)</Label>
                <JsonEditor
                  value={
                    Object.keys(localConfig).length > 0
                      ? JSON.stringify(localConfig, null, 2)
                      : ''
                  }
                  onChange={(val) => {
                    try {
                      const parsed = JSON.parse(val);
                      setLocalConfig(parsed);
                      onUpdateStep(step.id, { config: parsed });
                    } catch {
                      // Allow invalid JSON while typing
                    }
                  }}
                  height={120}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="timeout">Timeout (seconds)</Label>
                <Input
                  id="timeout"
                  type="number"
                  value={step.timeoutSeconds ?? ''}
                  onChange={(e) =>
                    onUpdateStep(step.id, { timeoutSeconds: parseInt(e.target.value) || undefined })
                  }
                  placeholder="300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="retry-count">Retry Count</Label>
                <Input
                  id="retry-count"
                  type="number"
                  min="0"
                  max="10"
                  value={step.maxRetries ?? ''}
                  onChange={(e) =>
                    onUpdateStep(step.id, { maxRetries: parseInt(e.target.value) || undefined })
                  }
                  placeholder="0"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delay-specific Settings */}
        {step.stepType === 'delay' && (
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm">Delay Settings</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="delay-duration">Duration</Label>
                <div className="flex gap-2">
                  <Input
                    id="delay-duration"
                    type="number"
                    min="1"
                    value={(localConfig.duration as number) || ''}
                    onChange={(e) =>
                      handleConfigChange('duration', parseInt(e.target.value) || undefined)
                    }
                    placeholder="5"
                  />
                  <Select
                    value={(localConfig.unit as string) || 'seconds'}
                    onValueChange={(value) => handleConfigChange('unit', value)}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="seconds">Seconds</SelectItem>
                      <SelectItem value="minutes">Minutes</SelectItem>
                      <SelectItem value="hours">Hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transform-specific Settings */}
        {step.stepType === 'transform' && (
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm">Transform Settings</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0 space-y-4">
              <div className="space-y-2">
                <Label>Transform Template</Label>
                <p className="text-xs text-muted-foreground">
                  Reshape the previous step&apos;s output for the next step. Insert
                  values with <code>{'{{field}}'}</code> — e.g.{' '}
                  <code>{'{{data.customer.name}}'}</code> or{' '}
                  <code>{'{{message}}'}</code>. If the result is valid JSON it&apos;s
                  passed on as structured data.
                </p>
                <CodeEditor
                  language="json"
                  value={(localConfig.expression as string) || ''}
                  onChange={(val) => handleConfigChange('expression', val)}
                  height={120}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Condition-specific Settings */}
        {step.stepType === 'condition' && (
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm">Conditions</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0 space-y-4">
              {localConditions.map((condition, index) => (
                <div
                  key={index}
                  className="p-3 bg-slate-50 rounded-lg space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">
                      Condition {index + 1}
                    </span>
                    <button
                      onClick={() => handleRemoveCondition(index)}
                      className="text-red-500 hover:text-red-700 text-xs"
                    >
                      Remove
                    </button>
                  </div>
                  <Input
                    value={condition.field}
                    onChange={(e) =>
                      handleUpdateCondition(index, { field: e.target.value })
                    }
                    placeholder="Field (e.g., status)"
                  />
                  <Select
                    value={condition.operator}
                    onValueChange={(value) =>
                      handleUpdateCondition(index, {
                        operator: value as WorkflowCondition['operator'],
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equals">Equals</SelectItem>
                      <SelectItem value="not_equals">Not Equals</SelectItem>
                      <SelectItem value="contains">Contains</SelectItem>
                      <SelectItem value="greater_than">Greater Than</SelectItem>
                      <SelectItem value="less_than">Less Than</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={String(condition.value)}
                    onChange={(e) =>
                      handleUpdateCondition(index, { value: e.target.value })
                    }
                    placeholder="Value"
                  />
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={handleAddCondition}
                className="w-full"
              >
                + Add Condition
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Error Handling */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm">Error Handling</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="on-error">On Error</Label>
              <Select
                value={(localConfig.onError as string) || 'fail'}
                onValueChange={(value) => handleConfigChange('onError', value)}
              >
                <SelectTrigger id="on-error">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fail">Fail Workflow</SelectItem>
                  <SelectItem value="continue">Continue to Next Step</SelectItem>
                  <SelectItem value="retry">Retry Step</SelectItem>
                  <SelectItem value="skip">Skip Step</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ConfigurationPanel;
